"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useRef, MouseEvent, TouchEvent } from "react";
import { Maximize2, MapPin, Calendar, Layers, ArrowRight, CircleCheck, MoveHorizontal, ShieldCheck, Droplets } from "lucide-react";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription, DialogHeader } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
interface Project {
  id: number;
  title: string;
  location: string;
  date: string;
  category: string;
  imageAfter: string;
  imageBefore: string;
  description: string;
  challenge: string;
  solution: string;
  techSpecs: string[];
  gridClass: string;
}
const projects: Project[] = [{
  id: 1,
  title: "Mecanizado de Engranaje Helicoidal",
  location: "Planta Sidor, Bolívar",
  date: "NOV 2023",
  category: "Mecanizado de Precisión",
  imageAfter: "https://mivgrupo.com/wp-content/uploads/2026/04/torneado-1.jpg?v=gxlzafn03su",
  imageBefore: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1632165/img/industrial_gear_before.png",
  description: "Fabricación integral de engranaje de gran escala para sistema de transmisión de laminación.",
  challenge: "Desgaste crítico de dientes por fatiga térmica. Tolerancia requerida de ±0.01mm en flancos.",
  solution: "Tallado con fresa matriz, tratamiento térmico por inducción y rectificado final CNC de alta precisión.",
  techSpecs: ["Acero AISI 4340", "Dureza: 55-60 HRC", "Diámetro: 1200mm", "Tolerancia IT6"],
  gridClass: "col-span-1 md:col-span-2 row-span-2"
}, {
  id: 2,
  title: "Reparación de Intercambiador",
  location: "Refinería Amuay, Falcón",
  date: "ENE 2024",
  category: "Mantenimiento Térmico",
  imageAfter: "https://mivgrupo.com/wp-content/uploads/2026/04/Techs_MIV_HE_Tiny.jpg?v=is2hqx85jp",
  imageBefore: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1632165/img/exchanger_repair_before.png",
  description: "Rehabilitación de haz de tubos y espejo de intercambiador de calor industrial.",
  challenge: "Corrosión severa en el espejo y fugas internas que comprometían la eficiencia del proceso.",
  solution: "Retubado completo, soldadura TIG certificada y mecanizado de caras de asiento para sellado hermético.",
  techSpecs: ["Tubos ASTM A179", "Prueba Hidrostática", "Sellado Metal-Metal"],
  gridClass: "col-span-1 row-span-1"
}, {
  id: 3,
  title: "MECANIZADO EN SITIO",
  location: "Planta El Tablazo, Zulia",
  date: "DIC 2023",
  category: "Mecanizado en Sitio",
  imageAfter: "https://mivgrupo.com/wp-content/uploads/2026/04/mecanizado-portatil-tiny.jpg?v=qdxe44l8mhi",
  imageBefore: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1632165/img/onsite_flange_before.png",
  description: "Rectificado de cara de brida de 48 pulgadas en reactor petroquímico sin desmontaje.",
  challenge: "Deformación por calor en la superficie de sellado. Imposibilidad de traslado a taller por dimensiones.",
  solution: "Despliegue de mandriladora portátil y refrentado de precisión para recuperación de diametros y planicidad.",
  techSpecs: ["Equipo Portátil CNC", "Acabado Phonographic", "Precisión: 0.05mm"],
  gridClass: "col-span-1 row-span-2"
}, {
  id: 4,
  title: "Reconstrucción de Reductor",
  location: "Cementera Vencemos, Zulia",
  date: "FEB 2024",
  category: "Transmisiones de Potencia",
  imageAfter: "https://mivgrupo.com/wp-content/uploads/2026/04/gearbox_rebuild_after-1.webp?v=edrxpiznjls",
  imageBefore: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1632165/img/gearbox_rebuild_before.png",
  description: "Overhaul completo de caja reductora de molino de crudo.",
  challenge: "Falla catastrófica de rodamientos y daño en alojamientos de carcasa.",
  solution: "Barrenado de alojamientos, fabricación de casquillos a medida y montaje interferido criogénico.",
  techSpecs: ["Capacidad: 500 HP", "Ajuste por Interferencia", "Garantía de Operación"],
  gridClass: "col-span-1 md:col-span-2 row-span-1"
}, {
  id: 5,
  title: "Fabricación de Eje de Turbina",
  location: "Represa Guri, Bolívar",
  date: "MAR 2024",
  category: "Fabricación Metalmecánica",
  imageAfter: "https://mivgrupo.com/wp-content/uploads/2026/04/turbine_shaft_after_Tiny.png?v=2q19jkcdij",
  imageBefore: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1632165/img/turbine_shaft_before.png",
  description: "Torneado de eje principal para unidad de generación hidroeléctrica.",
  challenge: "Longitud crítica de 3 metros con requisitos de concentricidad extremadamente rigurosos.",
  solution: "Torneado entre centros con lunetas fijas y control dimensional láser durante el proceso.",
  techSpecs: ["Longitud: 6000mm", "Material: SAE 1045", "Rugosidad: Ra 0.8"],
  gridClass: "col-span-1 row-span-1"
}];
const BeforeAfterSlider = ({
  before,
  after,
  alt
}: {
  before: string;
  after: string;
  alt: string;
}) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isHovering, setIsHovering] = useState(true);
  const containerRef = useRef<HTMLDivElement>(null);
  const handleMove = (clientX: number) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
      const percent = Math.max(0, Math.min(x / rect.width * 100, 100));
      setSliderPosition(percent);
    }
  };
  const onMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    setIsHovering(true);
    handleMove(e.clientX);
  };
  const onTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    setIsHovering(true);
    handleMove(e.touches[0].clientX);
  };
  return <div data-section-id="60156" ref={containerRef} className="relative w-full h-full overflow-hidden cursor-col-resize group select-none" onMouseMove={onMouseMove} onMouseLeave={() => setIsHovering(false)} onTouchMove={onTouchMove}>
      
      <img src={after} alt={`After ${alt}`} className="absolute inset-0 w-full h-full object-cover" />
      
      
      <div className="absolute top-4 right-4 bg-primary/90 text-primary-foreground text-[10px] font-mono px-2 py-1 rounded-sm backdrop-blur-sm z-10 pointer-events-none">
        FINALIZADO
      </div>

      
      <div className="absolute inset-0 w-full h-full overflow-hidden" style={{
      clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`
    }}>
        <img src={before} alt={`Before ${alt}`} className="absolute inset-0 w-full h-full object-cover max-w-none" style={{
        width: '100%',
        height: '100%'
      }} />
        
        
        <div className="absolute top-4 left-4 bg-secondary/80 text-secondary-foreground text-[10px] font-mono px-2 py-1 rounded-sm backdrop-blur-sm z-10 pointer-events-none">
          ESTADO INICIAL
        </div>
      </div>

      
      <div className="absolute top-0 bottom-0 w-1 bg-primary cursor-col-resize z-20 shadow-[0_0_15px_rgba(255,165,0,0.5)] flex items-center justify-center" style={{
      left: `${sliderPosition}%`
    }}>
        <div className="w-8 h-8 bg-primary rounded-sm shadow-lg flex items-center justify-center text-primary-foreground -ml-[14px]">
          <MoveHorizontal className="w-4 h-4" />
        </div>
      </div>

      
      <div className="hidden">
        <div className="bg-background/95 text-foreground px-4 py-2 rounded-sm text-xs font-mono font-bold shadow-2xl flex items-center gap-2 border border-border">
          <MoveHorizontal className="w-4 h-4 text-primary" />
          COMPARAR EJECUCIÓN
        </div>
      </div>
    </div>;
};
const ProjectCard = ({
  project,
  index
}: {
  project: Project;
  index: number;
}) => {
  return <Dialog>
      <DialogTrigger asChild>
        <div className="transition-all duration-700 delay-[0ms] transform opacity-100 translate-y-0 col-span-1 row-span-2 h-auto md:!h-[750px] md:col-span-2" data-index={index}>
          
  <div className="relative h-64 md:h-full min-h-[300px] w-full">
  <img src={project.imageAfter} alt={project.title} className="h-full object-cover !w-[825px] md:!h-[600px]" />          
            
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-background/95 via-background/60 to-transparent p-6 pt-12 translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
              <div className="flex justify-between items-end">
                <div>
                  <div className="flex items-center gap-2 text-primary text-xs font-mono mb-1">
                    <SafeHydrate><Calendar className="w-3 h-3" /></SafeHydrate>
                    <span>{project.date}</span>
                    <span className="text-muted-foreground">|</span>
                    <MapPin className="w-3 h-3" />
                    <span>{project.location}</span>
                  </div>
                  <h3 className="text-foreground font-default font-bold text-lg md:text-xl leading-tight mb-1 uppercase tracking-tight">
                    {project.title}
                  </h3>
                  <p className="text-muted-foreground text-sm font-mono">
                    {project.category}
                  </p>
                </div>
                <div className="bg-primary/10 p-2 rounded-sm backdrop-blur-md text-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300 hover:bg-primary hover:text-primary-foreground">
                  <Maximize2 className="w-5 h-5" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogTrigger>

      <DialogContent className="max-w-4xl p-0 overflow-hidden bg-background border-border rounded-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 h-full max-h-[90vh] overflow-y-auto md:overflow-hidden">
          
          <div className="relative h-64 md:h-full bg-muted">
             <img src={project.imageAfter} alt={project.title} className="w-full h-full object-cover" />
             <div className="absolute bottom-4 left-4 bg-primary text-primary-foreground px-3 py-1 text-xs font-bold rounded-sm font-mono">
               PIEZA FINALIZADA
             </div>
          </div>

          
          <div className="p-6 md:p-8 flex flex-col h-full overflow-y-auto">
            <DialogHeader className="mb-6">
              <div className="flex items-center gap-2 text-primary font-mono text-sm font-bold mb-2">
                <ShieldCheck className="w-4 h-4" />
                RESULTADOS GARANTIZADOS – IMPACTO OPERACIONAL
              </div>
              <DialogTitle className="text-2xl md:text-3xl font-default font-bold text-foreground uppercase tracking-tighter">
                {project.title}
              </DialogTitle>
              <DialogDescription className="flex items-center gap-4 mt-2 text-muted-foreground font-mono text-xs">
                <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {project.location}</span>
                <span className="flex items-center gap-1"><SafeHydrate><Calendar className="w-3 h-3" /></SafeHydrate> {project.date}</span>
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6">
              <div>
                <h4 className="text-xs font-bold text-foreground uppercase tracking-widest mb-2 flex items-center gap-2 font-default">
                  <Droplets className="w-4 h-4 text-primary" />
                  DESAFÍO INDUSTRIAL
                </h4>
                <p className="text-muted-foreground text-sm leading-relaxed font-default">
                  {project.challenge}
                </p>
              </div>

              <div>
                <h4 className="text-xs font-bold text-foreground uppercase tracking-widest mb-2 flex items-center gap-2 font-default">
                  <Layers className="w-4 h-4 text-primary" />
                  SOLUCIÓN DE MÁQUINAS DE ALTA PRECISIÓN
                </h4>
                <p className="text-muted-foreground text-sm leading-relaxed font-default">
                  {project.solution}
                </p>
              </div>

              <div className="bg-muted/30 p-4 rounded-sm border border-border/50">
                <h4 className="text-xs font-bold text-foreground mb-3 font-mono tracking-widest uppercase">ESPECIFICACIONES TÉCNICAS</h4>
                <ul className="grid grid-cols-1 gap-2">
                  {project.techSpecs.map((spec, i) => <li key={i} data-index={i} className="flex items-center gap-2 text-sm text-muted-foreground font-mono">
                      <CircleCheck className="w-4 h-4 text-primary shrink-0" />
                      {spec}
                    </li>)}
                </ul>
              </div>

              <div className="pt-4 mt-auto">
                <Button className="w-full rounded-sm" asChild>
                  <Link to="#">
                    Solicitar Evaluación Técnica <ArrowRight className="w-4 h-4 ml-2" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>;
};
export default function WaterArmoredProjects() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  return <section id="proyectos" className="py-10 md:py-32 bg-background dark relative overflow-hidden" ref={sectionRef}>
      
      
      <div className="absolute -left-20 top-40 w-96 h-96 bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute -right-20 bottom-40 w-96 h-96 bg-primary/10 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        
        <div className={`max-w-3xl mb-6 md:mb-20 transition-all duration-700 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-primary/10 text-primary text-xs font-mono font-bold mb-6 border border-primary/20 tracking-widest uppercase">
            <ShieldCheck className="w-3 h-3" />
            EFICIENCIA INDUSTRIAL MÁXIMA
          </div>
          <h2 className="font-default font-bold text-foreground tracking-tighter mb-6 uppercase !text-4xl">SOLUCIONES DE MECANIZADO Y FABRICACIÓN PARA LA INDUSTRIA PESADA</h2>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl font-default">
            Impulsamos la eficiencia operativa en los sectores de Oil & Gas, Petroquímica e Industria Pesada a través de mecanizados de alta precisión y soluciones de fabricación robustas, diseñadas para garantizar la continuidad de sus procesos críticos.
          </p>
        </div>

        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 auto-rows-auto md:auto-rows-[minmax(300px,auto)] grid-flow-dense">
          {projects.map((project, index) => <div key={project.id} className={`transition-all duration-700 delay-[${index * 100}ms] transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} ${project.gridClass}`} data-index={index}>
              <ProjectCard project={project} index={index} />
            </div>)}
          
          
          <div className={`col-span-1 row-span-1 flex flex-col justify-center items-center p-8 bg-primary text-primary-foreground rounded-sm text-center border border-primary/20 shadow-xl transition-all duration-700 delay-500 transform ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <ShieldCheck className="w-12 h-12 mb-4 text-primary-foreground" />
            <h3 className="text-xl font-default font-bold mb-2 uppercase tracking-tight">¿Necesita Soluciones Industriales a Medida?</h3>
            <p className="text-primary-foreground/90 text-sm mb-6 font-default">
              Analizamos sus planos y requerimientos técnicos para ofrecer una solución de mecanizado definitiva.
            </p>
            <Button variant="secondary" asChild className="w-full rounded-sm font-mono font-bold">
              <Link to="/#formulario-cotizacion-industrial">
                SOLICITAR CONSULTA TÉCNICA
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}